import * as Linking from 'expo-linking';
import { openAuthSessionAsync } from 'expo-web-browser';
import { Platform } from 'react-native';

import { firebaseAnalyticsAdapter } from '@/adapters/analytics';
import { createUserAwareAnalyticsAdapter } from '@/adapters/analytics/createUserAwareAnalyticsAdapter';
import { getCurrentAccountId } from '@/adapters/analytics/currentAccountId';
import { getCurrentUserId, setCurrentUserId } from '@/adapters/analytics/currentUserId';
import { generateCodeChallenge, generateCodeVerifier } from '@/adapters/auth/pkce';
import { apolloClient, registerLogoutHandler } from '@/adapters/graphql/apolloClient';
import { sentryLogger } from '@/adapters/logger/sentryLogger';
import { storage } from '@/adapters/storage/secureStorage';
import {
  ExchangeAuthorizationCodeDocument,
  GetAuthorizationLinkDocument,
  type Platform as ClientPlatform,
  RevokeAccessTokenDocument,
} from '@/generated/usermobile-bff/graphql';
import { createAuthStore } from '@/stores/createAuthStore';
import { useDeviceRegistrationStore } from '@/stores/useDeviceRegistrationStore';
import { useVoiceRecordingStore } from '@/stores/useVoiceRecordingStore';
import type { AuthAdapters } from '@/stores/createAuthStore';

const REDIRECT_URI = Linking.createURL('auth/callback');

const clientPlatform: ClientPlatform = Platform.OS === 'web' ? 'WEB' : 'NATIVE';

const openBrowserAuthSession = async (url: string, redirectUrl: string) => {
  if (Platform.OS !== 'android') {
    const result = await openAuthSessionAsync(url, redirectUrl);

    if (result.type === 'success') {
      return { type: 'success' as const, url: result.url };
    }

    return { type: result.type as 'cancel' | 'dismiss' };
  }

  const state = {
    capturedRedirectUrl: null as string | null,
  };

  const subscription = Linking.addEventListener('url', ({ url: eventUrl }) => {
    if (!eventUrl.startsWith(redirectUrl)) {
      return;
    }

    state.capturedRedirectUrl = eventUrl;
  });

  try {
    const result = await openAuthSessionAsync(url, redirectUrl);

    if (state.capturedRedirectUrl) {
      return { type: 'success' as const, url: state.capturedRedirectUrl };
    }

    if (result.type === 'success') {
      return { type: 'success' as const, url: result.url };
    }

    return { type: result.type as 'cancel' | 'dismiss' };
  } finally {
    subscription.remove();
  }
};

const productionAdapters: AuthAdapters = {
  secureStore: storage,
  analytics: createUserAwareAnalyticsAdapter(
    firebaseAnalyticsAdapter,
    () => getCurrentUserId(),
    () => getCurrentAccountId(),
  ),
  bff: {
    getAuthorizationLink: async (codeChallenge, codeChallengeMethod) => {
      const { data } = await apolloClient.query({
        query: GetAuthorizationLinkDocument,
        variables: { input: { codeChallenge, codeChallengeMethod, platform: clientPlatform } },
        fetchPolicy: 'no-cache',
      });

      if (!data?.authorizationLink) {
        throw new Error('auth.errors.failedToGetAuthLink');
      }

      return data.authorizationLink;
    },
    exchangeAuthorizationCode: async (state, code, codeVerifier) => {
      const { data } = await apolloClient.mutate({
        mutation: ExchangeAuthorizationCodeDocument,
        variables: { input: { state, code, codeVerifier } },
      });

      if (!data) {
        throw new Error('auth.errors.loginNoData');
      }

      return data.exchangeAuthorizationCode;
    },
    revokeAccessToken: async () => {
      const { data } = await apolloClient.mutate({ mutation: RevokeAccessTokenDocument });

      if (data?.revokeAccessToken !== true) {
        throw new Error('revokeAccessToken: mutation returned false');
      }
    },
  },
  browser: {
    openAuthSession: openBrowserAuthSession,
  },
  pkce: {
    generateCodeChallenge,
    generateCodeVerifier,
  },
  logger: sentryLogger,
  onBeforeLogout: async () => {
    await useDeviceRegistrationStore.getState().unregister();
    await apolloClient.clearStore();
    await useVoiceRecordingStore.getState().reset();
  },
};

const useAuthStore = createAuthStore(productionAdapters, REDIRECT_URI);

registerLogoutHandler(() => {
  setCurrentUserId(null);
  useAuthStore.setState({
    token: null,
    refreshToken: null,
    isAuthenticated: false,
    error: null,
  });
});

export { useAuthStore };
